// cw: Individual TY page — shows user's own pending requests + historical given/received
import React, { useEffect, useState } from 'react';
import ApiClient from '../../methods/api/apiClient';
import { useSelector } from 'react-redux';
import { FaHandshake, FaTrophy, FaInbox, FaHistory } from 'react-icons/fa';
import loader from '../../methods/loader';
import Layout from '../../components/global/layout';

const TestimonialsIndividual = () => {
    const user = useSelector((state: any) => state.user);
    const [stats, setStats] = useState<any>(null);
    const [myRequests, setMyRequests] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'pending' | 'history'>('pending');

    const displayLabel = stats?.label || user?.testimonialLabel || 'Thank Yous';

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        loader(true);
        try {
            const [statsRes, reqRes] = await Promise.all([
                ApiClient.get('api/testimonial/stats'),
                ApiClient.get('api/testimonial/my-requests')
            ]);

            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }
            if (reqRes.success) {
                setMyRequests(reqRes.data || []);
            }
        } catch (error: any) {
            console.error('Error loading data:', error);
        } finally {
            loader(false);
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <Layout>
                <div className='mb-6'>
                    <h3 className="text-2xl font-semibold text-[#111827]">
                        {user?.testimonialLabel || 'Thank Yous'} Individual
                    </h3>
                    <p className="text-sm font-normal text-[#75757A]">Loading...</p>
                </div>
                <div className="text-center py-8">
                    <img src="/assets/img/loader.gif" className="pageLoader" alt="Loading..." />
                </div>
            </Layout>
        );
    }

    const pendingRequests = myRequests.filter(r => r.status === 'submitted' || r.status === 'deferred');
    const decidedRequests = myRequests.filter(r => r.status !== 'submitted' && r.status !== 'deferred');

    return (
        <Layout>
            <div className="pprofile1">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h3 className="text-2xl font-semibold text-[#111827]">
                            {displayLabel} Individual
                        </h3>
                        <p className="text-sm font-normal text-[#75757A]">
                            Your personal {displayLabel.toLowerCase()} — pending requests and history
                        </p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Given</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {stats?.givenCount || 0}
                                </h3>
                            </div>
                            <FaHandshake className="text-4xl text-orange-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Received</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {stats?.receivedCount || 0}
                                </h3>
                            </div>
                            <FaTrophy className="text-4xl text-green-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Pending Requests</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {pendingRequests.length}
                                </h3>
                            </div>
                            <FaInbox className="text-4xl text-blue-500 opacity-50" />
                        </div>
                    </div>
                </div>

                {/* View Toggle */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setViewMode('pending')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'pending'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaInbox /> Pending Requests {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                    </button>
                    <button
                        onClick={() => setViewMode('history')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'history'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaHistory /> History
                    </button>
                </div>

                {/* Pending Requests */}
                {viewMode === 'pending' && (
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-4 border-b">
                            <h4 className="text-lg font-semibold">My Pending {displayLabel} Requests</h4>
                            <p className="text-sm text-gray-500">
                                {displayLabel} requests you've submitted that are awaiting review
                            </p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Note</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingRequests.map((req: any) => (
                                        <tr key={req._id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm">
                                                {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{req.receiverName || '-'}</td>
                                            <td className="px-4 py-3 text-sm capitalize">{req.thankYouType ? req.thankYouType.replace(/_/g, ' ') : '-'}</td>
                                            <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={req.note}>
                                                {req.note || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    req.status === 'deferred'
                                                        ? 'bg-yellow-100 text-yellow-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {req.status === 'deferred' ? 'Deferred' : 'Submitted'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {pendingRequests.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                No pending requests
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Also show decided requests below */}
                        {decidedRequests.length > 0 && (
                            <>
                                <div className="p-4 border-t border-b bg-gray-50">
                                    <h5 className="text-md font-semibold text-gray-700">Past Request Decisions</h5>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Note</th>
                                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {decidedRequests.map((req: any) => {
                                                const statusColors: any = {
                                                    accepted: 'bg-green-100 text-green-700',
                                                    declined: 'bg-red-100 text-red-700',
                                                    expired: 'bg-gray-100 text-gray-500',
                                                };
                                                return (
                                                    <tr key={req._id} className="border-t hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm">
                                                            {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">{req.receiverName || '-'}</td>
                                                        <td className="px-4 py-3 text-sm capitalize">{req.thankYouType ? req.thankYouType.replace(/_/g, ' ') : '-'}</td>
                                                        <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={req.note}>
                                                            {req.note || '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || 'bg-gray-100 text-gray-500'}`}>
                                                                {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* History View — Given and Received */}
                {viewMode === 'history' && (
                    <div className="space-y-6">
                        {/* Given */}
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 border-b">
                                <h4 className="text-lg font-semibold">{displayLabel} I've Given ({stats?.givenCount || 0})</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.givenTo || []).map((item: any, i: number) => (
                                            <tr key={i} className="border-t hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    {item.testimonialDate ? new Date(item.testimonialDate).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {item.fullName || item.email || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!stats?.givenTo || stats.givenTo.length === 0) && (
                                            <tr>
                                                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                                                    No {displayLabel.toLowerCase()} given yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Received */}
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 border-b">
                                <h4 className="text-lg font-semibold">{displayLabel} I've Received ({stats?.receivedCount || 0})</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">From</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.receivedFrom || []).map((item: any, i: number) => (
                                            <tr key={i} className="border-t hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    {item.testimonialDate ? new Date(item.testimonialDate).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {item.fullName || item.email || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!stats?.receivedFrom || stats.receivedFrom.length === 0) && (
                                            <tr>
                                                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                                                    No {displayLabel.toLowerCase()} received yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default TestimonialsIndividual;
